import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Button } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';

const PdfPageCard = ({ pdfDoc, pageNumber, totalPages }) => {
    const canvasRef = useRef(null);
    const [rendering, setRendering] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        let renderTask = null;

        const renderPage = async () => {
            try {
                setRendering(true);
                setError(null);
                const page = await pdfDoc.getPage(pageNumber);
                if (!isMounted || !canvasRef.current) return;

                // High DPI rendering for crystal clear text & graphics
                const scale = 2.0;
                const viewport = page.getViewport({ scale });
                const canvas = canvasRef.current;
                const context = canvas.getContext('2d');

                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport
                };

                renderTask = page.render(renderContext);
                await renderTask.promise;
                if (isMounted) {
                    setRendering(false);
                }
            } catch (err) {
                if (err.name !== 'RenderingCancelledException') {
                    console.error(`Error rendering page ${pageNumber}:`, err);
                    if (isMounted) {
                        setError('Failed to render page');
                        setRendering(false);
                    }
                }
            }
        };

        renderPage();

        return () => {
            isMounted = false;
            if (renderTask) {
                renderTask.cancel();
            }
        };
    }, [pdfDoc, pageNumber]);

    return (
        <Box mb={5} width="100%">
            <Box
                borderRadius="16px"
                border="1px solid #e2e8f0"
                bgcolor="#ffffff"
                overflow="hidden"
                style={{
                    boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.08), 0 4px 6px -2px rgba(15, 23, 42, 0.03)',
                    transition: 'box-shadow 0.2s ease-in-out'
                }}
            >
                {/* Clean page header banner */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={3}
                    py={1.5}
                    bgcolor="#f8fafc"
                    borderBottom="1px solid #f1f5f9"
                >
                    <Typography variant="caption" style={{ fontWeight: 800, color: '#475569', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        MSPL ESG Strategic Assessment • Page {pageNumber}
                    </Typography>
                    <Typography
                        variant="caption"
                        style={{
                            fontWeight: 700,
                            color: '#2563eb',
                            backgroundColor: '#eff6ff',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            border: '1px solid #dbeafe'
                        }}
                    >
                        Page {pageNumber} of {totalPages}
                    </Typography>
                </Box>

                {/* Page canvas area */}
                <Box
                    position="relative"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor="#ffffff"
                    minHeight={rendering ? 450 : 'auto'}
                    p={0}
                >
                    {rendering && (
                        <Box position="absolute" display="flex" flexDirection="column" alignItems="center">
                            <CircularProgress size={28} style={{ color: '#3b82f6', marginBottom: 12 }} />
                            <Typography variant="caption" style={{ fontWeight: 600, color: '#64748b' }}>
                                Rendering Page {pageNumber}...
                            </Typography>
                        </Box>
                    )}
                    {error && (
                        <Box py={6} textAlign="center">
                            <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                                {error}
                            </Typography>
                        </Box>
                    )}
                    <canvas
                        ref={canvasRef}
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            opacity: rendering ? 0.2 : 1,
                            transition: 'opacity 0.25s ease-in-out'
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const PdfPagesViewer = ({ pdfUrl }) => {
    const [pdfDoc, setPdfDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(0);

    const loadPdfDocument = () => {
        setLoading(true);
        setError(null);

        const initPdf = () => {
            const pdfjsLib = window['pdfjs-dist/build/pdf'] || window.pdfjsLib;
            if (!pdfjsLib) {
                setError("PDF library could not be loaded.");
                setLoading(false);
                return;
            }

            pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

            const loadingTask = pdfjsLib.getDocument(pdfUrl);
            loadingTask.promise
                .then(doc => {
                    setPdfDoc(doc);
                    setTotalPages(doc.numPages);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Error loading PDF document:", err);
                    setError("Failed to load PDF pages.");
                    setLoading(false);
                });
        };

        if (window['pdfjs-dist/build/pdf'] || window.pdfjsLib) {
            initPdf();
        } else {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
            script.onload = () => {
                initPdf();
            };
            script.onerror = () => {
                setError("Failed to load PDF library script.");
                setLoading(false);
            };
            document.head.appendChild(script);
        }
    };

    useEffect(() => {
        if (!pdfUrl) return;
        loadPdfDocument();
    }, [pdfUrl]);

    if (loading) {
        return (
            <Box p={6} textAlign="center" bgcolor="#f8fafc" borderRadius="16px" border="1px dashed #cbd5e1" my={3}>
                <CircularProgress size={34} style={{ color: '#2563eb', marginBottom: 16 }} />
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#0f172a' }}>
                    Loading Document Pages...
                </Typography>
                <Typography variant="body2" style={{ color: '#64748b', marginTop: 4 }}>
                    Formatting PDF pages seamlessly into the screen display
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={5} textAlign="center" bgcolor="#fef2f2" borderRadius="16px" border="1px solid #fecaca" my={3}>
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>
                    {error}
                </Typography>
                <Box display="flex" justifyContent="center" gap="12px" mt={2}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={loadPdfDocument}
                        startIcon={<RefreshIcon />}
                        style={{ textTransform: 'none', fontWeight: 700, borderColor: '#ef4444', color: '#ef4444' }}
                    >
                        Retry Loading
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box width="100%">
            <Box width="100%">
                {Array.from({ length: totalPages }, (_, idx) => (
                    <PdfPageCard
                        key={idx + 1}
                        pdfDoc={pdfDoc}
                        pageNumber={idx + 1}
                        totalPages={totalPages}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default PdfPagesViewer;
